/**
 * RFC 8785 (JSON Canonicalization Scheme) for the values a signing payload holds,
 * and the two digests judge mode shows. Used ONLY to re-check, in the browser,
 * what the bank already stored and the passkey already signed — never to build a
 * payload, never to make a challenge (the bank canonicalises once and stores the
 * bytes; the client never re-serialises and expects to be believed).
 *
 * Keys sort by UTF-16 code units (JavaScript's own string order), strings escape
 * as `JSON.stringify` does (ES2015+ is the JCS escaping), numbers use the ES
 * number-to-string (the contract carries none: amounts are decimal strings).
 */
export function canonical(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map((v) => canonical(v)).join(",")}]`;
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}`;
  }
  throw new TypeError(`not a JSON value: ${typeof value}`);
}

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource));
}

export function hex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function b64url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
